// /admin/divergence-alerts — lista filtrável e paginada com bulk-resolve.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowRight,
  Ban,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Link2,
  Lock,
  LockOpen,
  type LucideIcon,
  MessageCircle,
  Plug,
  RefreshCw,
  Search,
  Trash2,
  X,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { formatBRDateTime } from '@control-center/shared';

import { cn } from '@control-center/ui';

import { PillButton } from '../../../components/clients/PillButton';
import { Field, Input, Select } from '../../../components/clients/form-primitives';
import { PageHelp } from '../../../components/common/PageHelp';
import { LinkClientModal } from '../../../components/divergences/LinkClientModal';
import {
  fetchDivergenceAlertTypes,
  fetchDivergenceAlerts,
  fetchIgnoredNumbers,
  ignoreAlertNumber,
  removeIgnoredNumber,
  resolveDivergenceAlert,
  resolveDivergenceAlertsBulk,
  testAsaasConnectivity,
} from '../../../lib/dashboard-api';
import { extractErrorMessage } from '../../../lib/errors';

const TYPE_LABEL: Record<string, string> = {
  saas_active_cc_cancelled: 'SaaS ativo / CC cancelado',
  cc_active_saas_cancelled: 'CC ativo / SaaS cancelado',
  price_mismatch: 'Divergência de preço',
  plan_mismatch: 'Divergência de plano',
  email_invalid: 'E-mail inválido',
  communication_failed: 'Falha de comunicação',
  webhook_silent: 'Webhook sem resposta',
  chargeback: 'Chargeback',
  client_long_overdue: 'Cliente em atraso longo',
  whatsapp_from_unknown: 'WhatsApp de número desconhecido',
  fiscal_orphan_webhook: 'Webhook fiscal órfão',
  // Fase 4 — automação de suspensão/reativação/provisioning (Story 12.9)
  auto_suspension_executed: 'Suspensão automática executada',
  auto_reactivation_executed: 'Reativação automática executada',
  provisioning_failed: 'Falha de provisioning',
  access_state_divergence: 'Divergência de estado de acesso',
};

// Story 12.9 AC5 — ícone + cor (severidade) por tipo da Fase 4. Default: AlertTriangle/warn.
const TYPE_SEVERITY: Record<string, { Icon: LucideIcon; cls: string }> = {
  auto_suspension_executed: { Icon: Lock, cls: 'text-info' }, // informativo (azul)
  auto_reactivation_executed: { Icon: LockOpen, cls: 'text-good' }, // informativo (verde)
  provisioning_failed: { Icon: XCircle, cls: 'text-bad' }, // crítico (vermelho)
  access_state_divergence: { Icon: AlertTriangle, cls: 'text-warn' }, // alto (laranja)
};

const PAGE_SIZE = 20;

type Filter = 'pending' | 'resolved';

interface WhatsappAlertMeta {
  fromNumber: string | null;
  suggestedName: string | null;
  messageCount: number;
  lastMessageAt: string | null;
}

/** Extrai os campos do metadata de um alerta whatsapp_from_unknown (Story 5.11). */
function getWhatsappMeta(metadata: unknown): WhatsappAlertMeta {
  const m = (metadata && typeof metadata === 'object' ? metadata : {}) as Record<string, unknown>;
  const raw =
    m.rawPayload && typeof m.rawPayload === 'object'
      ? (m.rawPayload as Record<string, unknown>)
      : null;
  const rawData =
    raw?.data && typeof raw.data === 'object' ? (raw.data as Record<string, unknown>) : null;
  return {
    fromNumber: typeof m.fromNumber === 'string' ? m.fromNumber : null,
    suggestedName: typeof rawData?.pushName === 'string' ? rawData.pushName : null,
    messageCount: typeof m.messageCount === 'number' ? m.messageCount : 1,
    lastMessageAt: typeof m.lastMessageAt === 'string' ? m.lastMessageAt : null,
  };
}

export function DivergenceAlertsPage(): JSX.Element {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Filter>('pending');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [linkAlert, setLinkAlert] = useState<{
    id: string;
    fromNumber: string;
    suggestedName: string | null;
  } | null>(null);
  const [showIgnored, setShowIgnored] = useState(false);

  const list = useQuery({
    queryKey: ['divergence-alerts', filter, typeFilter, search, page],
    queryFn: () =>
      fetchDivergenceAlerts({
        resolved: filter === 'resolved',
        type: typeFilter || undefined,
        q: search || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
    staleTime: 15_000,
  });

  const typesQ = useQuery({
    queryKey: ['divergence-alert-types'],
    queryFn: () => fetchDivergenceAlertTypes(),
    staleTime: 60_000,
  });

  const resolveM = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      resolveDivergenceAlert(id, notes),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['divergence-alerts'] });
      qc.invalidateQueries({ queryKey: ['divergence-alert-types'] });
      qc.invalidateQueries({ queryKey: ['dashboard', 'divergence'] });
    },
  });

  const bulkM = useMutation({
    mutationFn: (input: { type?: string; q?: string; notes?: string }) =>
      resolveDivergenceAlertsBulk(input),
    onSuccess: ({ data }) => {
      qc.invalidateQueries({ queryKey: ['divergence-alerts'] });
      qc.invalidateQueries({ queryKey: ['divergence-alert-types'] });
      qc.invalidateQueries({ queryKey: ['dashboard', 'divergence'] });
      toast.success(`${data.resolved} divergência(s) resolvida(s) em lote.`);
    },
    onError: (err) => {
      toast.error(extractErrorMessage(err, 'Não conseguimos resolver em lote.'));
    },
  });

  const ignoredQ = useQuery({
    queryKey: ['ignored-numbers'],
    queryFn: () => fetchIgnoredNumbers(),
    enabled: showIgnored,
    staleTime: 30_000,
  });

  const ignoreNumberM = useMutation({
    mutationFn: (alertId: string) => ignoreAlertNumber(alertId),
    onSuccess: ({ data }) => {
      invalidateAll();
      qc.invalidateQueries({ queryKey: ['ignored-numbers'] });
      toast.success(
        `Número ignorado permanentemente — ${data.resolvedCount} divergência(s) resolvida(s).`,
      );
    },
    onError: (err) => {
      toast.error(extractErrorMessage(err, 'Não conseguimos ignorar o número.'));
    },
  });

  const removeIgnoredM = useMutation({
    mutationFn: (id: string) => removeIgnoredNumber(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ignored-numbers'] });
      toast.success('Número removido da lista de ignorados.');
    },
    onError: (err) => {
      toast.error(extractErrorMessage(err, 'Não conseguimos remover.'));
    },
  });

  function invalidateAll(): void {
    qc.invalidateQueries({ queryKey: ['divergence-alerts'] });
    qc.invalidateQueries({ queryKey: ['divergence-alert-types'] });
    qc.invalidateQueries({ queryKey: ['dashboard', 'divergence'] });
  }

  function handleIgnoreNumber(alertId: string, fromNumber: string): void {
    const ok = window.confirm(
      `Ignorar PERMANENTEMENTE o número ${fromNumber}? Mensagens futuras dele não vão mais gerar divergência.`,
    );
    if (!ok) return;
    ignoreNumberM.mutate(alertId);
  }

  async function handleResolve(id: string): Promise<void> {
    const notes = window.prompt('Notas sobre a resolução (opcional):', '') ?? undefined;
    try {
      await resolveM.mutateAsync({ id, notes });
      toast.success('Divergência resolvida.');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos resolver.'));
    }
  }

  async function handleDismiss(id: string, kind: string): Promise<void> {
    try {
      await resolveM.mutateAsync({ id, notes: `Ignorado pelo operador (${kind})` });
      toast.success('Divergência ignorada.');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos ignorar.'));
    }
  }

  function handleBulkIgnore(): void {
    const label = typeFilter ? (TYPE_LABEL[typeFilter] ?? typeFilter) : 'visíveis';
    const ok = window.confirm(
      `Ignorar TODAS as divergências pendentes do filtro atual (${label})?`,
    );
    if (!ok) return;
    bulkM.mutate({
      type: typeFilter || undefined,
      q: search || undefined,
      notes: `Ignorado em lote pelo operador (${label})`,
    });
  }

  function handleBulkResolve(): void {
    const label = typeFilter ? (TYPE_LABEL[typeFilter] ?? typeFilter) : 'visíveis';
    const notes = window.prompt(
      `Notas pra resolver TODAS as divergências pendentes (${label}):`,
      '',
    );
    if (notes === null) return;
    bulkM.mutate({
      type: typeFilter || undefined,
      q: search || undefined,
      notes: notes || `Resolvido em lote (${label})`,
    });
  }

  function applySearch(): void {
    setSearch(searchInput.trim());
    setPage(1);
  }

  function clearFilters(): void {
    setTypeFilter('');
    setSearchInput('');
    setSearch('');
    setPage(1);
  }

  const items = list.data?.data.items ?? [];
  const total = list.data?.data.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const types = typesQ.data?.data ?? [];

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-start gap-2">
          <div>
            <h1 className="m-0 text-[26px] font-bold tracking-[-0.025em] text-ink">Divergências</h1>
            <p className="mt-1 text-[13px] text-muted">
              Sinais operacionais que precisam atenção (cobranças anormais, sync de SaaS, etc).
            </p>
          </div>
          <PageHelp id="admin-divergence" />
        </div>
        <div className="flex flex-wrap gap-2">
          <PillButton
            icon={<Ban size={14} strokeWidth={2} />}
            onClick={() => setShowIgnored((v) => !v)}
          >
            Números ignorados
          </PillButton>
          <PillButton
            icon={<RefreshCw size={14} strokeWidth={2} />}
            onClick={() => void list.refetch()}
          >
            Recarregar
          </PillButton>
        </div>
      </header>

      {/* Story 5.11 AC8 — lista de números WhatsApp ignorados permanentemente */}
      {showIgnored && (
        <div className="rounded-[14px] border border-line bg-panel">
          <div className="border-b border-line bg-panel-2 px-4 py-3 text-[11px] uppercase tracking-wider text-muted-3">
            Números WhatsApp ignorados — não geram divergência
          </div>
          {ignoredQ.isLoading && <p className="px-4 py-3 text-[13px] text-muted">Carregando…</p>}
          {!ignoredQ.isLoading && (ignoredQ.data?.data.length ?? 0) === 0 && (
            <p className="px-4 py-3 text-[13px] text-muted">
              Nenhum número ignorado. Use &ldquo;Ignorar sempre este número&rdquo; num alerta de
              WhatsApp desconhecido.
            </p>
          )}
          {(ignoredQ.data?.data.length ?? 0) > 0 && (
            <ul className="m-0 list-none divide-y divide-line p-0">
              {ignoredQ.data?.data.map((n) => (
                <li key={n.id} className="flex items-center justify-between gap-2 px-4 py-2.5">
                  <div className="min-w-0">
                    <span className="tnum text-[13px] font-medium text-ink">{n.number}</span>
                    <span className="ml-2 text-[11px] text-muted-3 tnum">
                      desde {formatBRDateTime(n.created_at)}
                    </span>
                    {n.notes && <span className="ml-2 text-[12px] text-muted">— {n.notes}</span>}
                  </div>
                  <PillButton
                    icon={<Trash2 size={13} strokeWidth={2} />}
                    onClick={() => removeIgnoredM.mutate(n.id)}
                    disabled={removeIgnoredM.isPending}
                  >
                    Remover
                  </PillButton>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Filtros */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <Field label="Status">
          {() => (
            <Select
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value as Filter);
                setPage(1);
              }}
            >
              <option value="pending">Pendentes</option>
              <option value="resolved">Resolvidas</option>
            </Select>
          )}
        </Field>

        <Field label="Tipo">
          {() => (
            <Select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Todos os tipos</option>
              {types.map((t) => (
                <option key={t.type} value={t.type}>
                  {TYPE_LABEL[t.type] ?? t.type} ({filter === 'pending' ? t.pending : t.resolved})
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label="Buscar">
          {({ inputId }) => (
            <div className="flex gap-1">
              <Input
                id={inputId}
                placeholder="Cliente ou notas…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applySearch()}
              />
              <PillButton
                icon={<Search size={14} strokeWidth={2} />}
                onClick={applySearch}
                disabled={list.isFetching}
              >
                Buscar
              </PillButton>
            </div>
          )}
        </Field>

        <div className="flex items-end">
          <PillButton
            icon={<X size={14} strokeWidth={2} />}
            onClick={clearFilters}
            disabled={!typeFilter && !search && filter === 'pending'}
          >
            Limpar filtros
          </PillButton>
        </div>
      </div>

      {/* Bulk actions */}
      {filter === 'pending' && total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-[10px] border border-warn/30 bg-warn/5 px-4 py-3">
          <p className="m-0 text-[12px] text-ink-2">
            {total} divergência{total === 1 ? '' : 's'} pendente
            {total === 1 ? '' : 's'}
            {typeFilter && <> do tipo &ldquo;{TYPE_LABEL[typeFilter] ?? typeFilter}&rdquo;</>}
            {search && <> contendo &ldquo;{search}&rdquo;</>}.
          </p>
          <div className="flex flex-wrap gap-2">
            <PillButton
              icon={<X size={13} strokeWidth={2} />}
              onClick={handleBulkIgnore}
              disabled={bulkM.isPending}
            >
              Ignorar todas
            </PillButton>
            <PillButton
              icon={<CheckCircle2 size={13} strokeWidth={2} />}
              onClick={handleBulkResolve}
              disabled={bulkM.isPending}
            >
              Resolver todas
            </PillButton>
          </div>
        </div>
      )}

      {list.isLoading && <p className="text-[13px] text-muted">Carregando…</p>}

      {!list.isLoading && items.length === 0 && (
        <div className="rounded-[14px] border border-good/40 bg-good/10 py-12 text-center text-[13px] text-ink">
          <CheckCircle2 size={28} strokeWidth={1.5} className="mx-auto mb-2 text-good" />
          {filter === 'pending'
            ? 'Tudo certo — nenhuma divergência pendente.'
            : 'Nenhuma divergência resolvida ainda.'}
        </div>
      )}

      {items.length > 0 && (
        <div className="overflow-hidden rounded-[14px] border border-line bg-panel">
          <div className="flex items-center justify-between border-b border-line bg-panel-2 px-4 py-3 text-[11px] uppercase tracking-wider text-muted-3">
            <span>
              Página {page} de {totalPages} · {total} no total
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="inline-flex h-6 w-6 items-center justify-center rounded-[6px] text-ink-2 hover:bg-panel-3 disabled:opacity-30"
                aria-label="Página anterior"
              >
                <ChevronLeft size={14} strokeWidth={2} />
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="inline-flex h-6 w-6 items-center justify-center rounded-[6px] text-ink-2 hover:bg-panel-3 disabled:opacity-30"
                aria-label="Próxima página"
              >
                <ChevronRight size={14} strokeWidth={2} />
              </button>
            </div>
          </div>
          <ul className="divide-y divide-line">
            {items.map((it) => (
              <li key={it.id} className="px-4 py-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {(() => {
                        const sev = TYPE_SEVERITY[it.type];
                        const Icon = sev?.Icon ?? AlertTriangle;
                        return (
                          <Icon
                            size={14}
                            strokeWidth={1.8}
                            className={cn(
                              it.resolved_at ? 'text-muted' : (sev?.cls ?? 'text-warn'),
                            )}
                          />
                        );
                      })()}
                      <span className="text-[13px] font-medium text-ink">
                        {TYPE_LABEL[it.type] ?? it.type}
                      </span>
                      {it.client_name && (
                        <span className="text-[12px] text-muted-2">· {it.client_name}</span>
                      )}
                    </div>
                    <p className="mt-1 text-[11px] text-muted-3 tnum">
                      detectada em {formatBRDateTime(it.detected_at)}
                      {it.resolved_at && <> · resolvida em {formatBRDateTime(it.resolved_at)}</>}
                    </p>
                    <DivergenceMetadata type={it.type} metadata={it.metadata} />
                    {!it.resolved_at && (
                      <DivergenceActions
                        type={it.type}
                        metadata={it.metadata}
                        clientId={it.client_id}
                        onLinkClient={() => {
                          const meta = getWhatsappMeta(it.metadata);
                          if (meta.fromNumber) {
                            setLinkAlert({
                              id: it.id,
                              fromNumber: meta.fromNumber,
                              suggestedName: meta.suggestedName,
                            });
                          }
                        }}
                        onIgnoreNumber={() => {
                          const meta = getWhatsappMeta(it.metadata);
                          if (meta.fromNumber) handleIgnoreNumber(it.id, meta.fromNumber);
                        }}
                        ignoreDisabled={ignoreNumberM.isPending}
                      />
                    )}
                    {it.notes && (
                      <p className="mt-2 text-[12px] italic text-muted">&ldquo;{it.notes}&rdquo;</p>
                    )}
                  </div>
                  {!it.resolved_at && (
                    <div className="flex flex-wrap items-center gap-2">
                      <PillButton
                        onClick={() => void handleDismiss(it.id, it.type)}
                        disabled={resolveM.isPending}
                        icon={<X size={13} strokeWidth={2} />}
                      >
                        Ignorar
                      </PillButton>
                      <PillButton
                        onClick={() => void handleResolve(it.id)}
                        disabled={resolveM.isPending}
                        icon={<CheckCircle2 size={13} strokeWidth={2} />}
                      >
                        Resolver
                      </PillButton>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Story 5.11 AC13 — modal de vínculo a cliente existente */}
      <LinkClientModal
        open={linkAlert !== null}
        onOpenChange={(open) => {
          if (!open) setLinkAlert(null);
        }}
        alert={linkAlert}
        onLinked={invalidateAll}
      />
    </div>
  );
}

function DivergenceMetadata({
  type,
  metadata,
}: {
  type: string;
  metadata: unknown;
}): JSX.Element | null {
  if (!metadata || typeof metadata !== 'object') return null;
  const m = metadata as Record<string, unknown>;

  if (type === 'whatsapp_from_unknown') {
    const fromNumber = typeof m.fromNumber === 'string' ? m.fromNumber : null;
    const bodyPreview = typeof m.bodyPreview === 'string' ? m.bodyPreview : null;
    const { messageCount, lastMessageAt } = getWhatsappMeta(metadata);
    return (
      <div className="mt-2 rounded-[8px] border border-line bg-panel-2 p-2 text-[12px]">
        <div className="flex items-center gap-1 text-muted-2">
          <MessageCircle size={11} strokeWidth={1.8} />
          {fromNumber ? <span className="tnum text-ink-2">{fromNumber}</span> : 'Número oculto'}
          {/* Story 5.11 AC3 — badge quando o mesmo número mandou mais de uma mensagem */}
          {messageCount > 1 && (
            <span className="rounded-full border border-warn/30 bg-warn/10 px-1.5 py-0.5 text-[10px] font-medium text-warn">
              {messageCount} mensagens
            </span>
          )}
        </div>
        {bodyPreview && (
          <p className="mt-1 m-0 line-clamp-2 text-ink-2">&ldquo;{bodyPreview}&rdquo;</p>
        )}
        {messageCount > 1 && lastMessageAt && (
          <p className="mt-1 m-0 text-[11px] text-muted-3 tnum">
            última em {formatBRDateTime(lastMessageAt)}
          </p>
        )}
      </div>
    );
  }

  if (type === 'webhook_silent') {
    const hours = typeof m.hours === 'number' ? m.hours : null;
    return hours ? (
      <p className="mt-1 text-[12px] text-muted">
        Último evento recebido há {hours}h. Abra Eventos recebidos pra investigar.
      </p>
    ) : null;
  }

  // Story 12.9 AC7 — falha de provisioning com link direto pra DLQ.
  if (type === 'provisioning_failed') {
    const action = typeof m.action === 'string' ? m.action : null;
    const saasSlug = typeof m.saas_slug === 'string' ? m.saas_slug : null;
    const attempts = typeof m.attempts_count === 'number' ? m.attempts_count : null;
    const errorMessage = typeof m.error_message === 'string' ? m.error_message : null;
    return (
      <div className="mt-2 rounded-[8px] border border-bad/30 bg-bad/5 p-2 text-[12px]">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-muted-2">
          {action && (
            <span>
              <span className="text-muted-3">ação:</span>{' '}
              <span className="text-ink-2">{action === 'revoke' ? 'revogar' : 'conceder'}</span>
            </span>
          )}
          {saasSlug && (
            <span>
              <span className="text-muted-3">saas:</span>{' '}
              <code className="text-ink-2">{saasSlug}</code>
            </span>
          )}
          {attempts != null && (
            <span>
              <span className="text-muted-3">tentativas:</span>{' '}
              <span className="text-ink-2 tnum">{attempts}</span>
            </span>
          )}
        </div>
        {errorMessage && <p className="mt-1 m-0 line-clamp-2 text-bad">{errorMessage}</p>}
        <Link
          to="/provisioning?tab=failed"
          className="mt-1 inline-flex items-center gap-1 text-[12px] font-medium text-accent underline-offset-2 hover:underline"
        >
          <Plug size={11} strokeWidth={1.8} />
          Ver na DLQ
        </Link>
      </div>
    );
  }

  const entries = Object.entries(m)
    .filter(([, v]) => typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean')
    .slice(0, 3);
  if (entries.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-2">
      {entries.map(([k, v]) => (
        <span key={k}>
          <span className="text-muted-3">{k}:</span> <span className="text-ink-2">{String(v)}</span>
        </span>
      ))}
    </div>
  );
}

/**
 * Botões de ação contextual por tipo de divergência.
 * Cada tipo conhecido tem 1-3 atalhos pra onde resolver/investigar.
 */
function DivergenceActions({
  type,
  metadata,
  clientId,
  onLinkClient,
  onIgnoreNumber,
  ignoreDisabled,
}: {
  type: string;
  metadata: unknown;
  clientId: string | null;
  onLinkClient?: () => void;
  onIgnoreNumber?: () => void;
  ignoreDisabled?: boolean;
}): JSX.Element | null {
  const m = (metadata && typeof metadata === 'object' ? metadata : {}) as Record<string, unknown>;
  const actions: { to: string; label: string }[] = [];

  switch (type) {
    case 'webhook_silent': {
      // Caso especial: webhook_silent tem botão que JÁ FAZ o teste (não navega)
      return <WebhookSilentActions />;
    }
    case 'whatsapp_from_unknown': {
      const fromNumber = typeof m.fromNumber === 'string' ? m.fromNumber : null;
      if (!fromNumber) {
        return (
          <div className="mt-2 flex flex-wrap gap-2">
            <ActionLink to="/clients" label="Abrir identidades" />
          </div>
        );
      }
      // Story 5.11 — vincular a cliente existente / criar novo / ignorar permanente
      return (
        <div className="mt-2 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onLinkClient}
            className="inline-flex items-center gap-1 rounded-[6px] border border-good/30 bg-good/10 px-2 py-1 text-[11px] font-medium text-good hover:bg-good/20"
          >
            <Link2 size={11} strokeWidth={2} />
            Vincular a cliente
          </button>
          <ActionLink
            to={`/identities/new?phone=${encodeURIComponent(fromNumber)}`}
            label="Criar cliente com esse número"
          />
          <button
            type="button"
            onClick={onIgnoreNumber}
            disabled={ignoreDisabled}
            className="inline-flex items-center gap-1 rounded-[6px] border border-line bg-panel-2 px-2 py-1 text-[11px] font-medium text-ink-2 hover:bg-panel-3 disabled:opacity-50"
          >
            <Ban size={11} strokeWidth={2} />
            Ignorar sempre este número
          </button>
        </div>
      );
    }
    case 'email_invalid':
    case 'client_long_overdue':
    case 'communication_failed': {
      if (clientId) {
        actions.push({ to: `/clients/${clientId}`, label: 'Abrir cliente' });
      } else {
        actions.push({ to: '/clients', label: 'Abrir identidades' });
      }
      break;
    }
    case 'chargeback': {
      if (clientId) actions.push({ to: `/clients/${clientId}`, label: 'Abrir cliente' });
      actions.push({ to: '/charges?scope=todas', label: 'Ver cobranças' });
      break;
    }
    case 'price_mismatch':
    case 'plan_mismatch':
    case 'saas_active_cc_cancelled':
    case 'cc_active_saas_cancelled': {
      if (clientId) actions.push({ to: `/clients/${clientId}`, label: 'Abrir cliente' });
      actions.push({ to: '/subscriptions', label: 'Ver assinaturas' });
      break;
    }
    case 'fiscal_orphan_webhook': {
      actions.push({ to: '/fiscal', label: 'Abrir notas fiscais' });
      actions.push({ to: '/webhook-events', label: 'Ver eventos recebidos' });
      break;
    }
  }

  if (actions.length === 0) return null;

  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {actions.map((a) => (
        <ActionLink key={a.to} to={a.to} label={a.label} />
      ))}
    </div>
  );
}

function ActionLink({ to, label }: { to: string; label: string }): JSX.Element {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1 rounded-[6px] border border-good/30 bg-good/10 px-2 py-1 text-[11px] font-medium text-good hover:bg-good/20"
    >
      {label}
      <ArrowRight size={11} strokeWidth={2} />
    </Link>
  );
}

/**
 * Ações específicas pra webhook_silent: ao invés de só linkar, faz um ping
 * direto na Asaas API e mostra o resultado inline (sucesso ou erro real).
 */
function WebhookSilentActions(): JSX.Element {
  const [result, setResult] = useState<{
    ok: boolean;
    httpStatus: number | null;
    responseTimeMs: number;
    errorMessage: string | null;
    accountName: string | null;
  } | null>(null);

  const testM = useMutation({
    mutationFn: testAsaasConnectivity,
    onSuccess: (data) => {
      setResult(data);
      if (data.ok) {
        toast.success(
          `Asaas respondeu OK em ${data.responseTimeMs}ms · conta ${data.accountName ?? '?'}`,
        );
      } else {
        toast.error(`Asaas falhou: ${data.errorMessage ?? 'erro desconhecido'}`);
      }
    },
    onError: (err) => {
      toast.error(`Não consegui testar: ${err instanceof Error ? err.message : String(err)}`);
    },
  });

  return (
    <div className="mt-2 space-y-2">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => testM.mutate()}
          disabled={testM.isPending}
          className="inline-flex items-center gap-1 rounded-[6px] border border-good/30 bg-good/10 px-2 py-1 text-[11px] font-medium text-good hover:bg-good/20 disabled:opacity-50"
        >
          <Plug size={11} strokeWidth={2} />
          {testM.isPending ? 'Testando…' : 'Testar conexão Asaas agora'}
        </button>
        <Link
          to="/webhook-events"
          className="inline-flex items-center gap-1 rounded-[6px] border border-line bg-panel-2 px-2 py-1 text-[11px] font-medium text-ink-2 hover:bg-panel-3"
        >
          Ver eventos recebidos
          <ArrowRight size={11} strokeWidth={2} />
        </Link>
      </div>

      {result && (
        <div
          className={cn(
            'rounded-[8px] border p-2 text-[12px]',
            result.ok
              ? 'border-good/30 bg-good/5 text-ink-2'
              : 'border-warn/30 bg-warn/5 text-ink-2',
          )}
        >
          {result.ok ? (
            <>
              ✅ Asaas <strong>{result.accountName}</strong> respondeu HTTP {result.httpStatus} em{' '}
              {result.responseTimeMs}ms. A conexão tá viva — provavelmente é só falta de cobranças
              recentes mesmo. Pode <strong>ignorar</strong> a divergência.
            </>
          ) : (
            <>
              ⚠️ Falhou{result.httpStatus ? ` (HTTP ${result.httpStatus})` : ''}:{' '}
              {result.errorMessage}
            </>
          )}
        </div>
      )}
    </div>
  );
}
